import { useRef, useEffect } from 'react';

/**
 * Tooltip banner anchors — set `UserBanner.tooltipAnchor` in admin to match one of these IDs.
 *
 * Composer / chat input
 * - model-selector — model picker (Auto / selected models)
 * - voice-button — voice conversation
 * - dictate-button — speech-to-text dictation
 * - composer-tools — + menu (attach files, tools, parameters)
 *
 * Sidebar navigation (discovery & retention)
 * - new-chat — header “new conversation” control
 * - files-nav — Files library
 * - memory-nav — Memory
 * - tasks-nav — Tasks
 * - models-nav — Models directory
 * - agents-nav — Custom agents
 * - images-nav — Image studio / create images
 *
 * Monetization
 * - sidebar-pro — Plus / upgrade strip above account
 */

const _anchors = new Map();
const _listeners = new Set();

function notifyListeners() {
  for (const fn of _listeners) fn();
}

export function registerAnchor(id, ref) {
  _anchors.set(id, ref);
  notifyListeners();
}

export function unregisterAnchor(id) {
  _anchors.delete(id);
  notifyListeners();
}

export function getAnchorRef(id) {
  return _anchors.get(id) || null;
}

export function subscribeAnchors(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function useTooltipAnchor(anchorId) {
  const ref = useRef(null);

  useEffect(() => {
    registerAnchor(anchorId, ref);
    return () => unregisterAnchor(anchorId);
  }, [anchorId]);

  return ref;
}
